import {
  Text,
  View,
  ScrollView,
  Pressable,
  LayoutChangeEvent,
} from "react-native";
import { Tabs, TabList, Tab } from "app/features/navigation/organisms/tabs";
import { SubTabBar } from "app/features/navigation/organisms/tabs/SubTabBar";
import {
  ChevronsLeftIcon,
  ChevronsRightIcon,
  ChevronDownIcon,
  Icon,
} from "ui/gluestack/icon";
import { useRef, useEffect, useMemo, useState, useCallback } from "react";
import { useRouter } from "expo-router";
import {
  findActiveTabIndices,
  type ResolvedTabItem,
} from "app/constants/tabConstants";

interface TabPanelProps {
  tabs?: ResolvedTabItem[];
  currentPath?: string;
  className?: string;
  hideTabs?: boolean;
}

export function ResidentTabPanel({
  tabs,
  currentPath,
  className,
  hideTabs,
}: TabPanelProps) {
  const scrollRef = useRef<ScrollView>(null);
  const router = useRouter();

  const { parentIndex } = useMemo(() => {
    if (!currentPath || !tabs) return { parentIndex: -1, childIndex: -1 };
    return findActiveTabIndices(tabs, currentPath);
  }, [currentPath, tabs]);

  const [expandedParentIndex, setExpandedParentIndex] = useState<number>(-1);
  const [containerWidth, setContainerWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);

  const showArrows = contentWidth > containerWidth;

  useEffect(() => {
    if (parentIndex >= 0 && tabs?.[parentIndex]?.children) {
      setExpandedParentIndex(parentIndex);
    }
  }, [parentIndex, tabs]);

  const handleContainerLayout = useCallback((e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width);
  }, []);

  const handleContentLayout = useCallback((e: LayoutChangeEvent) => {
    setContentWidth(e.nativeEvent.layout.width);
  }, []);

  const handleParentPress = useCallback((index: number) => {
    setExpandedParentIndex((prev) => (prev === index ? -1 : index));
  }, []);

  const expandedParentTab =
    expandedParentIndex >= 0 ? tabs?.[expandedParentIndex] : undefined;

  const isAtStart =
    parentIndex === 0 || (parentIndex === -1 && expandedParentIndex <= 0);
  const isAtEnd = parentIndex === (tabs?.length ?? 0) - 1;

  useEffect(() => {
    if (scrollRef.current && tabs && parentIndex >= 0) {
      const tabWidth = 120;
      const offset = parentIndex * tabWidth;
      scrollRef.current.scrollTo({ x: offset, animated: true });
    }
  }, [parentIndex, tabs]);

  const handleFirstTab = () => {
    if (tabs && tabs.length > 0) {
      router.push(tabs[0]!.path);
    }
  };

  const handleLastTab = () => {
    if (tabs && tabs.length > 0) {
      router.push(tabs[tabs.length - 1]!.path);
    }
  };

  if (hideTabs) return null;

  // Check if any parent tab has children (to reserve space for submenu)
  const hasAnyParentWithChildren = tabs?.some((tab) => tab.children) ?? false;

  return (
    <View className={`${className} mb-4`}>
      <View
        className="flex flex-row items-center justify-between border-b border-gray-200"
        onLayout={handleContainerLayout}
      >
        {showArrows && (
          <Pressable
            disabled={isAtStart}
            onPress={handleFirstTab}
            className={`min-w-[48px] min-h-[48px] items-center justify-center ${isAtStart ? "opacity-30" : ""}`}
          >
            <Icon as={ChevronsLeftIcon} width={16} height={16} />
          </Pressable>
        )}
        <ScrollView
          ref={scrollRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          scrollEventThrottle={16}
          className="flex-1"
        >
          <View onLayout={handleContentLayout}>
            <Tabs className="self-end">
              <TabList>
                {tabs?.map((tab, index) => {
                  const isSelected = index === parentIndex;
                  const isExpanded =
                    index === expandedParentIndex && index !== parentIndex;
                  const hasChildren = !!tab.children;

                  return (
                    <Tab
                      key={tab.path}
                      value={tab.path}
                      isActive={isSelected}
                      isExpanded={isExpanded}
                      onPress={
                        hasChildren ? () => handleParentPress(index) : undefined
                      }
                    >
                      <View className="flex-row items-center gap-x-1">
                        <Text
                          className={`text-base ${
                            isSelected
                              ? "text-typography-900 font-semibold"
                              : isExpanded
                                ? "text-primary-600"
                                : "text-typography-400"
                          }`}
                        >
                          {tab.label}
                        </Text>
                        {hasChildren && (
                          <Icon
                            as={ChevronDownIcon}
                            width={12}
                            height={12}
                            className={
                              isSelected
                                ? "text-typography-900"
                                : isExpanded
                                  ? "text-primary-600"
                                  : "text-typography-400"
                            }
                          />
                        )}
                      </View>
                    </Tab>
                  );
                })}
              </TabList>
            </Tabs>
          </View>
        </ScrollView>
        {showArrows && (
          <Pressable
            disabled={isAtEnd}
            onPress={handleLastTab}
            className={`min-w-[48px] min-h-[48px] items-center justify-center ${isAtEnd ? "opacity-30" : ""}`}
          >
            <Icon as={ChevronsRightIcon} width={16} height={16} />
          </Pressable>
        )}
      </View>
      {/* Reserve space for submenu to avoid layout shifts */}
      {hasAnyParentWithChildren && (
        <View className="min-h-[48px]">
          {expandedParentTab?.children && currentPath && (
            <SubTabBar
              subTabs={expandedParentTab.children}
              currentPath={currentPath}
            />
          )}
        </View>
      )}
    </View>
  );
}
